import { decodeHtmlEntities } from "../../../../../../common/helpers/decodeHtmlEntities";
import type {
  RawEnrichedAttribute,
  RawEnrichedAttributeTerm,
} from "../../../../../../types";
import type {
  EnrichedAttributeTransformed,
  EnrichedAttributeTermTransformed,
} from "../../../types/productApiTypes";

/**
 * A pure helper function to transform the raw enriched attributes data from the API.
 * It handles the translation of both attribute names and term names.
 *
 * @param rawAttributes The `attributes` array from the `enriched_attributes` extension.
 * @param shouldTranslate A boolean indicating if translations should be applied.
 * @returns A new array of transformed attribute objects.
 */
export const transformEnrichedAttributes = (
  rawAttributes: RawEnrichedAttribute[] | undefined,
  shouldTranslate: boolean
): EnrichedAttributeTransformed[] => {
  if (!Array.isArray(rawAttributes)) {
    return [];
  }

  return rawAttributes.map(
    (rawAttr: RawEnrichedAttribute): EnrichedAttributeTransformed => {
      // Determine and decode Attribute Name
      let attrName = rawAttr.name;
      if (shouldTranslate && rawAttr.translated_name) {
        attrName = rawAttr.translated_name;
      }
      attrName = decodeHtmlEntities(attrName);

      const termsTransformed: EnrichedAttributeTermTransformed[] = (
        rawAttr.terms || []
      ).map(
        (
          rawTerm: RawEnrichedAttributeTerm
        ): EnrichedAttributeTermTransformed => {
          // Determine and decode Term Name
          let termName = rawTerm.name;
          if (shouldTranslate && rawTerm.translated_name) {
            termName = rawTerm.translated_name;
          }
          termName = decodeHtmlEntities(termName);

          return {
            id: rawTerm.id,
            name: termName,
            slug: rawTerm.slug,
            type: rawTerm.type,
            buttonColor: rawTerm["button-color"],
            buttonText: termName,
            buttonImage: rawTerm["button-image"],
          };
        }
      );

      return {
        id: rawAttr.id,
        name: attrName,
        taxonomy: rawAttr.taxonomy,
        hasVariations: rawAttr.has_variations,
        displayType: rawAttr.display_type,
        terms: termsTransformed,
      };
    }
  );
};
